import { getEffectiveInstructionSteps } from './instructionStepUtils';
import type { InstructionStepDto, LessonDetailDto } from './types';

/** atest.Constants.UnitState — GetUnits sends unit.State?.Key */
export const UnitStateKey = {
    New: '10',
    Reviewing: '20',
    Ready: '30',
    InProgress: '40',
    LessonsCompleted: '45',
    Complete: '50',
    Assessing: '60'
} as const;

/** atest.Constants.LessonState — wire sends lesson.State?.Key */
export const LessonStateKey = {
    Ready: '30',
    InProgress: '40',
    Completed: '50'
} as const;

export const LessonDeliveryModeKey = {
    InAppInstruction: 'InAppInstruction',
    ExternalResource: 'ExternalResource'
} as const;

/** atest.Constants.InstructionStepKind — wire sends step.StepKind?.Key */
export const InstructionStepKindKey = {
    Lesson: 'Lesson',
    WrapUp: 'WrapUp',
    Review: 'Review'
} as const;

export const BRIDGE_STEP_DURATION_MINUTES = 5;

export function isLessonInProgressKey(state: string | undefined): boolean {
    return state === LessonStateKey.InProgress;
}

export function isLessonCompletedKey(state: string | undefined): boolean {
    return state === LessonStateKey.Completed;
}

export function isLessonReadyKey(state: string | undefined): boolean {
    return state === LessonStateKey.Ready;
}

export function isLessonReady(lesson: LessonDetailDto): boolean {
    return isLessonReadyKey(lesson.e251_LessonState);
}

export function isLessonInProgress(lesson: LessonDetailDto): boolean {
    return isLessonInProgressKey(lesson.e251_LessonState);
}

export function isLessonCompleted(lesson: LessonDetailDto): boolean {
    return lesson.e251_LessonCompletionDate != null || isLessonCompletedKey(lesson.e251_LessonState);
}

/** Alias kept for existing call sites (e.g. unit header progress). */
export function isLessonCompletedDto(lesson: LessonDetailDto): boolean {
    return isLessonCompleted(lesson);
}

export function isLessonStartedNotCompletedDto(lesson: LessonDetailDto): boolean {
    if (isLessonCompleted(lesson)) return false;
    if (isLessonInProgress(lesson)) return true;
    return getLessonSteps(lesson).some(s => s.e251_IsCompleted);
}

/** True once the learner has started the lesson (in progress, completed, or any step done). */
export function isLessonStarted(lesson: LessonDetailDto): boolean {
    if (isLessonCompleted(lesson) || isLessonInProgress(lesson)) return true;
    return getLessonSteps(lesson).some(s => s.e251_IsCompleted);
}

export function getLessonSteps(lesson: LessonDetailDto): InstructionStepDto[] {
    return getEffectiveInstructionSteps(lesson.e251_InstructionSteps);
}

export function getContentSteps(lesson: LessonDetailDto): InstructionStepDto[] {
    return getLessonSteps(lesson).filter(
        s => !s.e251_StepKind || s.e251_StepKind === InstructionStepKindKey.Lesson
    );
}

export function getPendingSteps(lesson: LessonDetailDto): InstructionStepDto[] {
    return getLessonSteps(lesson).filter(s => !s.e251_IsCompleted);
}

export function isExternalResourceLesson(lesson: LessonDetailDto): boolean {
    return lesson.e251_DeliveryMode === LessonDeliveryModeKey.ExternalResource;
}

export function hasOpenSession(lesson: LessonDetailDto): boolean {
    if (isExternalResourceLesson(lesson) && lesson.e251_ActiveExternalSessionId != null) {
        return true;
    }

    return getLessonSteps(lesson).some(
        s =>
            s.e251_StepSessionId != null &&
            (s.e251_DetailedInstructions ?? '').trim().length > 0 &&
            !s.e251_IsCompleted
    );
}

export function getOpenSessionId(lesson: LessonDetailDto): number | null {
    if (isExternalResourceLesson(lesson) && lesson.e251_ActiveExternalSessionId != null) {
        return lesson.e251_ActiveExternalSessionId;
    }

    if (!hasOpenSession(lesson)) return null;
    const sessionIds = getLessonSteps(lesson)
        .filter(
            s =>
                s.e251_StepSessionId != null &&
                (s.e251_DetailedInstructions ?? '').trim().length > 0 &&
                !s.e251_IsCompleted
        )
        .map(s => s.e251_StepSessionId as number);
    return sessionIds.length > 0 ? Math.max(...sessionIds) : null;
}

export function getSessionSteps(lesson: LessonDetailDto, sessionId: number): InstructionStepDto[] {
    return getLessonSteps(lesson).filter(s => s.e251_StepSessionId === sessionId);
}

export function getSessionDeliverySteps(
    lesson: LessonDetailDto,
    sessionId?: number | null
): InstructionStepDto[] {
    const sid = sessionId ?? getOpenSessionId(lesson);
    if (sid == null) return [];
    return getSessionSteps(lesson, sid).filter(
        s => (s.e251_DetailedInstructions ?? '').trim().length > 0
    );
}

export function isLessonContentComplete(lesson: LessonDetailDto): boolean {
    if (isExternalResourceLesson(lesson)) {
        return isLessonCompleted(lesson);
    }

    const content = getContentSteps(lesson);
    return content.length > 0 && content.every(s => s.e251_IsCompleted);
}

export function sumStepDurationMinutes(steps: InstructionStepDto[]): number {
    return steps.reduce((sum, s) => sum + (s.e251_DurationMinutes ?? 0), 0);
}

/** Human-readable labels derived from lesson State?.Key (print / UI only). */
export function formatLessonStateKeyLabel(stateKey: string | undefined): string {
    switch (stateKey) {
        case LessonStateKey.Completed:
            return 'Completed';
        case LessonStateKey.InProgress:
            return 'In Progress';
        case LessonStateKey.Ready:
            return 'Ready';
        default:
            return (stateKey ?? '').trim() || 'Unknown';
    }
}
